import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Package } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ProductChoice } from '@/features/asset-mgr/catalogGuard'
import {
  answerCreateProduct,
  answerProductChoice,
  useCurrentCreateProduct,
  useCurrentProductChoice,
  type ProductChoiceAnswer,
  type ProductChoiceMode,
  type ProductChoiceProgress,
} from '@/features/asset-mgr/productChoiceStore'
import type { SnowflakeId } from '@/types/snowflake'
import { ProductCreateDrawer } from './ProductCreateDrawer'
import { usePermission } from '@/hooks/usePermission'

export interface ProductChoiceDialogProps {
  open: boolean
  choice: ProductChoice | null
  mode: ProductChoiceMode
  /** 批量建采购时：第几个问题 / 共几个。 */
  progress?: ProductChoiceProgress
  onAnswer: (answer: ProductChoiceAnswer | null) => void
}

/**
 * 换到新问题后按钮先锁一会儿：连点两下时，第二下不能落到下一个问题上（同一位置的按钮）。
 */
export const ANSWER_GUARD_MS = 500

/**
 * 服务端说「目录里可能已有」时让用户选：用其中一个，或说都不是（建产品 =「是新的」，建采购 =「都不是」）。
 * 关掉 = 不回答（调用方当取消）。
 */
export function ProductChoiceDialog({ open, choice, mode, progress, onAnswer }: ProductChoiceDialogProps) {
  const { t } = useTranslation('assets')
  const { t: tc } = useTranslation('productCatalog')
  // 采购模式「建产品」会打开建产品抽屉：没有建产品权限就不给这个出口（只能选候选或记成自由文本）
  const { hasPermission } = usePermission()
  const canCreateProduct = hasPermission('asset-mgr:product:create')
  const [selected, setSelected] = useState<SnowflakeId | null>(null)
  // 每个问题都是新挂载（Host 用 key），这里只管挂载后的短锁
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setArmed(true), ANSWER_GUARD_MS)
    return () => clearTimeout(timer)
  }, [])

  const candidates = choice?.candidates ?? []
  const picked = candidates.find((c) => c.id === selected) ?? null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onAnswer(null) }}>
      {/* DialogContent 是 grid：列宽默认按内容最小宽撑，一个长产品名（truncate 的 nowrap）就把整列撑出弹窗、
          描述和候选全溢出去（2026-10-24 走查）。列宽锁成 minmax(0,1fr)，长名字才按 truncate 截断 */}
      <DialogContent className="max-w-md grid-cols-[minmax(0,1fr)]" data-testid="product-choice-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'product' ? t('productChoice.titleProduct') : t('productChoice.titlePurchase')}
          </DialogTitle>
          {progress && progress.total > 1 && (
            <p className="text-xs font-medium text-muted-foreground" data-testid="product-choice-progress">
              {t('productChoice.progress', { index: progress.index, total: progress.total })}
            </p>
          )}
          <DialogDescription className="break-words">
            {mode === 'product'
              ? t('productChoice.descriptionProduct', { asked: choice?.asked ?? '' })
              : tc('choice.descriptionPurchase', { asked: choice?.asked ?? '' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5" role="radiogroup" aria-label={t('productChoice.candidatesLabel')}>
          {candidates.map((c) => {
            const active = c.id === selected
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelected(c.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left hover:bg-accent',
                  active ? 'border-primary bg-accent' : 'border-border',
                )}
              >
                <Package className="h-4 w-4 flex-none text-muted-foreground" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-foreground truncate" title={c.name}>{c.name}</span>
                  <span className="block text-xs text-muted-foreground truncate">
                    {c.category ?? t('productChoice.noCategory')}
                  </span>
                </span>
                {active && <Check className="h-4 w-4 flex-none text-primary" />}
              </button>
            )
          })}
        </div>

        {mode === 'product' && choice?.suggestedCategory && (
          <p className="text-xs text-muted-foreground">
            {t('productChoice.suggestedCategory', { path: choice.suggestedCategory.path })}
          </p>
        )}

        {mode === 'purchase' && !canCreateProduct && (
          <p className="text-xs text-muted-foreground">{tc('create.noPermissionChoice')}</p>
        )}

        <DialogFooter className="gap-2 sm:flex-wrap sm:gap-2">
          {(mode === 'product' || canCreateProduct) && (
            <Button
              variant="outline"
              disabled={!armed}
              onClick={() => onAnswer({ kind: 'new', notThese: candidates.map((c) => c.id) })}
            >
              {mode === 'product' ? t('productChoice.btnItIsNew') : tc('choice.btnCreateProduct')}
            </Button>
          )}
          <Button
            disabled={!picked || !armed}
            onClick={() => { if (picked) onAnswer({ kind: 'use', product: picked }) }}
          >
            {t('productChoice.btnUseSelected')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** 挂在 Providers 里：回答 `askProductChoice` 的队列，以及 `askCreateProduct` 的建产品抽屉。 */
export function ProductChoiceHost() {
  const request = useCurrentProductChoice()
  const create = useCurrentCreateProduct()
  return (
    <>
      {/* key：每个问题重新挂载，选中项、按钮短锁都从头来 */}
      {request && (
        <ProductChoiceDialog
          key={request.id}
          open
          choice={request.choice}
          mode={request.mode}
          progress={request.progress}
          onAnswer={(answer) => answerProductChoice(request.id, answer)}
        />
      )}
      {create && (
        <ProductCreateDrawer
          key={create.id}
          open
          initialText={create.text}
          progress={create.progress}
          onClose={() => answerCreateProduct(create.id, null)}
          onCreated={(p) => answerCreateProduct(create.id, p)}
        />
      )}
    </>
  )
}
